import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react'

/**
 * The `@` picker list and its keyboard model, shared by every mention-capable input
 * (`MentionTextarea` and `RichTextEditor`), so they behave identically: Slack's model.
 *
 * - ArrowDown / ArrowUp move the highlight (wrapping), the first match is highlighted.
 * - Enter or Tab inserts the highlighted person; Escape closes the list.
 * - Keys pressed mid IME composition are left alone — Enter there commits the composition.
 *
 * Focus never leaves the input. The list is a `listbox` the input points at with
 * `aria-activedescendant`, which is what lets a screen reader announce the highlighted name.
 */

export type MentionKeyAction = 'next' | 'prev' | 'choose' | 'close'

interface KeyLike {
  key: string
  keyCode?: number
  isComposing?: boolean
  shiftKey?: boolean
  altKey?: boolean
  metaKey?: boolean
  ctrlKey?: boolean
}

/** What a key means while the picker is open, or null if the input should handle it. */
export function mentionKeyAction(event: KeyLike): MentionKeyAction | null {
  // 229 is what Safari reports for keys that belong to an in-progress composition.
  if (event.isComposing || event.keyCode === 229) return null
  if (event.altKey || event.metaKey || event.ctrlKey) return null
  switch (event.key) {
    case 'ArrowDown':
      return 'next'
    case 'ArrowUp':
      return 'prev'
    case 'Enter':
      return event.shiftKey ? null : 'choose'
    case 'Tab':
      return event.shiftKey ? null : 'choose'
    case 'Escape':
      return 'close'
    default:
      return null
  }
}

/**
 * Highlight state for a list of `count` matches. It returns to the first match whenever
 * `resetKey` (the typed query) changes, and stays put otherwise — so a re-sync that does
 * not change the query (a keyup, a selection update) never throws the highlight away.
 */
export function useMentionHighlight(count: number, resetKey: string) {
  const [state, setState] = useState({ key: resetKey, index: 0 })
  // Derived, not synced: a stale key simply reads as "back at the top".
  const index = state.key === resetKey && state.index < count ? state.index : 0

  const set = (next: number) => setState({ key: resetKey, index: next })
  return {
    highlighted: index,
    setHighlighted: set,
    move: (delta: 1 | -1) => {
      if (count === 0) return
      set((index + delta + count) % count)
    },
  }
}

/**
 * Escape while the list is open must close the list and nothing else.
 *
 * Radix dialogs and sheets (the card drawer, for one) listen for Escape on the document in
 * the CAPTURE phase, so they would close before the input's own handler ever ran. A
 * window-level capture listener runs first and stops the event there.
 */
export function useEscapeCloses(
  open: boolean,
  inputRef: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
  })
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing) return
      const input = inputRef.current
      if (!input || !(event.target instanceof Node) || !input.contains(event.target)) return
      event.preventDefault()
      event.stopPropagation()
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, inputRef])
}

/** A stable id for the listbox and a function naming each option, for aria-activedescendant. */
export function useMentionIds() {
  const listId = `mention-list-${useId().replace(/[^\w-]/g, '')}`
  const optionId = useCallback((i: number) => `${listId}-option-${i}`, [listId])
  return { listId, optionId }
}
